import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { AutoCompleteSelectionItem } from '../../component/autocomplete-remote/autocomplete-selection-item';
import { AutoCompleteSelectionState } from '../../component/autocomplete-remote/autocomplete-service.interface';
import { ModelBase } from '../../model/model-base';
import { MessageService } from '../message.service';
import { ProgressBarService } from '../progress-bar.service';
import { Inject } from '@angular/core';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { getDownloadFileName } from '../../helper/download-file-name';

export abstract class BffServiceBase {
  protected readonly serviceEndpoint: string;
}
export abstract class DataServiceBase {
  protected readonly endpoint: string;

  constructor(
    protected readonly client: HttpClient,
    protected readonly notification: MessageService,
    protected readonly progressBar: ProgressBarService,
    protected readonly config: IAppConfig,
    serviceEndPoint: string
  ) {
    this.endpoint = `${this.config.apiEndpointV2}/${serviceEndPoint}`;
  }

  prepareFilter(filter: any): any {
    const keys = Object.keys(filter);
    keys.forEach(key => {
      filter[key] = this.mapFilterValue(key, filter[key]);
    });
    return filter;
  }

  mapFilterValue(key: string, value: any): any {
    return value instanceof ModelBase ? value.id : value;
  }

  protected getSelectionItemWithOptions<TData = any>(
    id: string,
    state: AutoCompleteSelectionState,
    mapping: (TData) => AutoCompleteSelectionItem<TData>
  ): Observable<AutoCompleteSelectionItem<TData> | null> {
    let uri = `${this.endpoint}/selectionItem/${id}`;
    if (state?.options) {
      uri += `?options=${state.options}`;
    }
    return this.client.get<TData>(uri).pipe(
      map(mapping),
      catchError((e: HttpErrorResponse) => {
        if (state?.options && e?.status === 404) {
          return of(null);
        }
        this.notification.showError(e);
        return [];
      })
    );
  }

  protected getSelectionWithOptions<TData = any, TOptions = any>(
    filter: string,
    options: TOptions,
    mapping: (TData) => AutoCompleteSelectionItem<TData>,
    page?: number
  ): Observable<AutoCompleteSelectionItem<TData>[]> {
    const queryArgs = {
      filter: filter,
      maxItems: 20,
    };
    if (options) queryArgs['options'] = options;
    if (page > 0) queryArgs['page'] = page;

    return this.client.post<TData[]>(`${this.endpoint}/selection`, queryArgs).pipe(
      map(data => data.map(mapping)),
      catchError(e => {
        this.notification.showError(e);
        throw e;
      })
    );
  }

  protected exportDataItemsFiltered(
    filter: any,
    includedIds: string[],
    excludedIds: string[],
    affectedItemsCount: number,
    defaultFileName: string,
    forcePageAction?: boolean
  ): Observable<File> {
    return this.exportDataItemsFilteredWithUrl(
      'export',
      'export-filter',
      filter,
      includedIds,
      excludedIds,
      affectedItemsCount,
      defaultFileName,
      forcePageAction
    );
  }

  protected exportDataItemsFilteredWithUrl(
    directExportUrl: string,
    filteredExportUrl: string,
    filter: any,
    includedIds: string[],
    excludedIds: string[],
    affectedItemsCount: number,
    defaultFileName: string,
    forcePageAction?: boolean
  ): Observable<File> {
    if (includedIds.length > 0 && !forcePageAction) {
      return this.exportDataItemsWithUrl(directExportUrl, includedIds, defaultFileName);
    }
    this.progressBar.start();
    return this.client
      .post(
        `${this.endpoint}/${filteredExportUrl}`,
        { filter: this.prepareFilter(filter), includedIds: includedIds, excludedIds: excludedIds, affectedItemsCount: affectedItemsCount },
        { observe: 'response', responseType: 'blob' }
      )
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, defaultFileName);
        }),
        catchError(error => {
          if (error.error instanceof Blob && error.error.type === 'application/json') {
            return from(error.error.text()).pipe(
              switchMap((text: unknown) => {
                const parsedError = JSON.parse(text as string);
                this.notification.showError(parsedError.message);
                return of(null);
              })
            );
          }
          this.notification.showError('ERROR.UNKNOWN');
          return of(null);
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  protected exportDataItems(ids: string[], defaultFileName: string): Observable<File> {
    return this.exportDataItemsWithUrl('export', ids, defaultFileName);
  }

  protected exportDataItemsWithUrl(url: string, ids: string[], defaultFileName: string): Observable<File> {
    this.progressBar.start();
    return this.client.post(`${this.endpoint}/${url}`, JSON.stringify(ids), { observe: 'response', responseType: 'blob' }).pipe(
      map((res: HttpResponse<Blob>) => {
        return this.toFile(res, defaultFileName);
      }),
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  protected exportDataItemsExcludePersonalData(ids: string[], defaultFileName: string): Observable<File> {
    this.progressBar.start();
    return this.client
      .post(`${this.endpoint}/export-no-personal-data`, JSON.stringify(ids), { observe: 'response', responseType: 'blob' })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, defaultFileName);
        }),
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  protected getPdf(path: string, params?: HttpParams): Observable<Blob> {
    return this.client.get(`${this.endpoint}/${path}`, { responseType: 'blob', params: params ? params : {} }).pipe(
      map(res => {
        return new Blob([res as BlobPart], { type: 'application/pdf' });
      })
    );
  }

  protected getFile(path: string, defaultName: string): Observable<File> {
    return this.client.get(path, { observe: 'response', responseType: 'blob' }).pipe(
      map((res: HttpResponse<Blob>) => {
        return this.toFile(res, defaultName);
      }),
      catchError(error => {
        this.notification.showError(error);
        return [];
      })
    );
  }

  protected postFile(path: string, body: unknown, defaultName: string): Observable<File> {
    return this.client.post(path, body, { observe: 'response', responseType: 'blob' }).pipe(
      map((res: HttpResponse<Blob>) => {
        return this.toFile(res, defaultName);
      }),
      catchError(error => {
        this.notification.showError(error);
        return [];
      })
    );
  }

  public bufferToFile(response: HttpResponse<ArrayBuffer>, defaultName: string, contentType: string): File {
    const fileName = getDownloadFileName(response.headers.get('content-disposition'), defaultName);
    return new File([response.body], fileName, { type: contentType });
  }

  protected toFile(response: HttpResponse<Blob>, defaultName: string): File {
    const fileName = getDownloadFileName(response.headers.get('content-disposition'), defaultName);
    return new File([response.body], fileName, { type: response.body.type });
  }
}
